import type { TourStep } from "../tour-steps";

/** Tours de página do domínio Conta/Configurações — cada um roda inteiro numa única rota.
 *  Páginas simples ganham só 1-2 passos — não força 3 onde não há o que explicar. */

export const EQUIPE_TOUR_STEPS: TourStep[] = [
  {
    id: "equipe-membros",
    route: "/equipe",
    target: '[data-tour="equipe-membros"]',
    title: "Quem faz parte da sua equipe",
    content: "Cada membro tem um papel (Owner, Admin, etc) que define o que ele pode ver e fazer na organização.",
    skipBeacon: true,
  },
  {
    id: "equipe-convidar",
    route: "/equipe",
    target: '[data-tour="equipe-convidar"]',
    title: "Convide um novo membro",
    content: "Gere um convite por e-mail — a pessoa entra direto na sua organização assim que aceitar.",
    skipBeacon: true,
    demo: "invite-link",
  },
];

export const ORGANIZACOES_TOUR_STEPS: TourStep[] = [
  {
    id: "organizacoes-visao-geral",
    route: "/organizacoes",
    target: '[data-tour="organizacoes-visao-geral"]',
    title: "O que pede atenção agora",
    content: "A soma de todas as suas organizações: pedidos atrasados, aguardando aceite, consultas de hoje, cobranças vencidas e não lidas. Clique num número pra ver só as organizações com ele; clique numa organização pra ver o detalhe sem trocar.",
    skipBeacon: true,
  },
  {
    id: "organizacoes-criar",
    route: "/organizacoes",
    target: '[data-tour="organizacoes-criar"]',
    title: "Crie uma nova organização",
    content: "Útil quando você abre uma nova clínica ou laboratório e quer manter os dados separados da conta pessoal.",
    skipBeacon: true,
    demo: "fill-form",
  },
];

export const APPS_PRO_TOUR_STEPS: TourStep[] = [
  {
    id: "apps-pro-grid",
    route: "/apps-pro",
    target: '[data-tour="apps-pro-grid"]',
    title: "Fique de olho nos apps profissionais",
    content: "Visualizador DICOM, IA pra raio-X e outros recursos avançados estão a caminho — essa página é atualizada conforme chegam.",
    skipBeacon: true,
  },
];

export const NOTIFICACOES_TOUR_STEPS: TourStep[] = [
  {
    id: "notificacoes-filtros",
    route: "/notificacoes",
    target: '[data-tour="notificacoes-filtros"]',
    title: "Todas ou só as não lidas",
    content: "Alterne entre os dois filtros pra focar no que ainda não foi visto.",
    skipBeacon: true,
  },
];

export const PERFIL_TOUR_STEPS: TourStep[] = [
  {
    id: "perfil-avatar",
    route: "/conta/perfil",
    target: '[data-tour="perfil-avatar"]',
    title: "Seu perfil",
    content: "Clique na foto pra trocar — ela aparece pra outras pessoas das suas organizações. Embaixo, seus números somando todas elas.",
    skipBeacon: true,
  },
  {
    id: "perfil-secoes",
    route: "/conta/perfil",
    target: '[data-tour="config-secoes"]',
    title: "Cada assunto numa seção",
    content: "Dados profissionais (nome, CRO, especialidades, biografia), suas organizações, contato, segurança, notificações, aparência e privacidade.",
    skipBeacon: true,
  },
];

export const CONFIG_AGENDA_TOUR_STEPS: TourStep[] = [
  {
    id: "config-agenda-secoes",
    route: "/configuracoes/agenda",
    target: '[data-tour="config-agenda-secoes"]',
    title: "Horários, visual, lembretes e mais",
    content: "Cada seção do menu ajusta uma parte diferente de como sua Agenda se comporta.",
    skipBeacon: true,
  },
  {
    id: "config-agenda-salvar",
    route: "/configuracoes/agenda",
    target: '[data-tour="config-agenda-salvar"]',
    title: "Salve suas alterações",
    content: "Depois de ajustar o que precisar, clique em Salvar — ou em Restaurar padrões pra voltar como era antes.",
    skipBeacon: true,
    demo: "fill-form",
  },
];

export const CONFIG_IMPORTAR_TOUR_STEPS: TourStep[] = [
  {
    id: "config-importar-secoes",
    route: "/configuracoes/importar",
    target: '[data-tour="config-importar-secoes"]',
    title: "Importe dados de outro sistema",
    content: "Pacientes, Agenda e Fichas clínicas podem ser importados de uma planilha — escolha a seção certa no menu.",
    skipBeacon: true,
  },
  {
    id: "config-importar-arquivo",
    route: "/configuracoes/importar",
    target: '[data-tour="config-importar-arquivo"]',
    title: "Arraste sua planilha aqui",
    content: "Depois de enviar o arquivo, você mapeia as colunas antes de confirmar a importação — nada é salvo sem essa etapa.",
    skipBeacon: true,
    demo: "list-add",
  },
];

export const CONFIG_NOTIFICACOES_TOUR_STEPS: TourStep[] = [
  {
    id: "config-notificacoes-tabs",
    route: "/conta/preferencias",
    target: '[data-tour="config-notificacoes-tabs"]',
    title: "Som e tipos de aviso",
    content: "Configure separadamente o som das notificações e quais tipos de aviso você quer receber.",
    skipBeacon: true,
  },
  {
    id: "config-notificacoes-som",
    route: "/conta/preferencias",
    target: '[data-tour="config-notificacoes-som"]',
    title: "Ative ou desative o som",
    content: "Útil em ambientes silenciosos, ou pra garantir que você não perca um aviso importante.",
    skipBeacon: true,
  },
];

export const CONFIG_ORGANIZACAO_TOUR_STEPS: TourStep[] = [
  {
    // As seções ficam sempre visíveis (sidebar no desktop, seletor no celular) — diferente dos
    // alvos internos, que só existem com a própria seção aberta.
    id: "config-secoes",
    route: "/configuracoes/dados",
    target: '[data-tour="config-secoes"]',
    title: "Configurações da organização",
    content:
      "Cada seção cuida de uma parte: dados cadastrais (nome, CNPJ, endereço), área de atuação (clínica/laboratório — muda o menu), especialidades, permissões e propriedade.",
    skipBeacon: true,
  },
  {
    id: "config-organizacao-nome",
    route: "/configuracoes/dados",
    target: '[data-tour="config-organizacao-nome"]',
    title: "Renomeie sua organização",
    content: "O nome aparece pra toda a equipe e pros parceiros conectados — altere sempre que precisar.",
    skipBeacon: true,
    demo: "fill-form",
  },
];

export const CONFIG_LAYOUT_TOUR_STEPS: TourStep[] = [
  {
    id: "config-layout-estilos",
    route: "/conta/preferencias",
    target: '[data-tour="config-layout-estilos"]',
    title: "Escolha o estilo do menu",
    content: "Compacto, Clássico ou Colorido — a mudança aplica na hora, sem precisar salvar.",
    skipBeacon: true,
  },
];
